import {
  Button,
  Checkbox,
  Form,
  FormItem,
  Input,
  InputNumber,
  Message,
  SearchPaginatedSelect,
  Select,
  TextArea,
  useForm,
} from "@/frontend/components/common";
import { useAuthStore } from "@/frontend/stores";
import { bettingPoolService, prizeTypeService, tournamentService } from "@/frontend/services";
import type {
  CreateBettingPoolRequestDTO,
  UpdateBettingPoolRequestDTO,
  TournamentResponseDTO,
  BettingPoolResponseDTO,
  BettingPoolWithTournamentResponseDTO,
  BettingPoolPrizeInputDTO,
  BettingPoolPrizeResponseDTO,
  PrizeTypeResponseDTO,
} from "@/shared/dto";
import { DEFAULT_POOL_CONFIG } from "@/shared/dto";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { DatePicker, Form as AntForm } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  FaStar,
  FaTrophy,
  FaFutbol,
  FaClock,
  FaGift,
  FaLock,
  FaExclamationTriangle,
  FaPlus,
  FaTimes,
} from "react-icons/fa";

type ResultRuleType = "exact_score" | "winner";

type BonusRuleType =
  | "bracket_winner"
  | "home_goals_exact"
  | "away_goals_exact"
  | "unique_bonus";

interface RuleState {
  enabled: boolean;
  points: number;
  priority: number;
}

const initialResultRules: Record<ResultRuleType, RuleState> = {
  exact_score: { enabled: true, points: 5, priority: 1 },
  winner: { enabled: true, points: 3, priority: 2 },
};

const initialBonusRules: Record<BonusRuleType, RuleState> = {
  home_goals_exact: { enabled: true, points: 1, priority: 0 },
  away_goals_exact: { enabled: true, points: 1, priority: 0 },
  unique_bonus: { enabled: true, points: 5, priority: 0 },
  bracket_winner: { enabled: false, points: 0, priority: 0 },
};

const disabledResultRules: Record<ResultRuleType, RuleState> = {
  exact_score: { enabled: false, points: 0, priority: 1 },
  winner: { enabled: false, points: 0, priority: 2 },
};

const disabledBonusRules: Record<BonusRuleType, RuleState> = {
  home_goals_exact: { enabled: false, points: 0, priority: 0 },
  away_goals_exact: { enabled: false, points: 0, priority: 0 },
  unique_bonus: { enabled: false, points: 0, priority: 0 },
  bracket_winner: { enabled: false, points: 0, priority: 0 },
};

const RESULT_RULE_LABELS: Record<ResultRuleType, string> = {
  exact_score: "Marcador exacto",
  winner: "Ganador",
};

const BONUS_RULE_LABELS: Record<BonusRuleType, string> = {
  home_goals_exact: "Goles local exactos",
  away_goals_exact: "Goles visitante exactos",
  unique_bonus: "Bonus único (marcador exacto único)",
  bracket_winner:
    "Predecir quien es el ganador en fase de eliminación directa si predice marcador de empate",
};

const RESULT_RULE_DESCRIPTIONS: Record<ResultRuleType, string> = {
  exact_score: "El usuario predice el resultado exacto del partido",
  winner: "El usuario predice solo el equipo ganador o empate",
};

const BONUS_RULE_DESCRIPTIONS: Record<BonusRuleType, string> = {
  home_goals_exact: "Puntos extra si el usuario acierta los goles del local",
  away_goals_exact: "Puntos extra si el usuario acierta los goles del visitante",
  unique_bonus: "Puntos extra si es el único que acierta el marcador exacto",
  bracket_winner: "",
};

type PrizePosition = 1 | 2 | 3;

const PRIZE_POSITIONS: PrizePosition[] = [1, 2, 3];

const PRIZE_POSITION_LABELS: Record<PrizePosition, string> = {
  1: "1er puesto",
  2: "2do puesto",
  3: "3er puesto",
};

interface FormValues {
  name: string;
  description?: string;
  tournamentId: string;
  seasonId: string;
  prize1TypeId: string;
  prize1Value?: string;
  prize1ImageUrl?: string;
  prize2TypeId?: string;
  prize2Value?: string;
  prize2ImageUrl?: string;
  prize3TypeId?: string;
  prize3Value?: string;
  prize3ImageUrl?: string;
  isPrivate: boolean;
}

function deriveVisiblePrizePositions(
  prizes: BettingPoolPrizeResponseDTO[] | undefined,
): PrizePosition[] {
  const positions = new Set<PrizePosition>([1]);

  for (const prize of prizes ?? []) {
    if (prize.position === 2 || prize.position === 3) {
      positions.add(prize.position);
    }
  }

  return PRIZE_POSITIONS.filter((position) => positions.has(position));
}

const MAX_PRIZE_IMAGE_BYTES = 2 * 1024 * 1024;

function nextAddablePrizePosition(visible: PrizePosition[]): PrizePosition | null {
  const max = Math.max(...visible);
  if (max >= 3) return null;
  return (max + 1) as PrizePosition;
}

function buildPrizesFromForm(values: FormValues): BettingPoolPrizeInputDTO[] {
  const prizes: BettingPoolPrizeInputDTO[] = [];

  for (const position of PRIZE_POSITIONS) {
    const typeId = values[`prize${position}TypeId` as keyof FormValues] as string | undefined;
    const prizeValue = values[`prize${position}Value` as keyof FormValues] as string | undefined;
    const prizeImageUrl = values[`prize${position}ImageUrl` as keyof FormValues] as
      | string
      | undefined;

    if (position > 1 && !typeId?.trim()) {
      continue;
    }

    prizes.push({
      position,
      prizeTypeId: typeId!.trim(),
      prizeValue: prizeValue?.trim() || "",
      prizeImageUrl: prizeImageUrl?.trim() || null,
    });
  }

  return prizes;
}

function mapPrizesToFormFields(prizes: BettingPoolPrizeResponseDTO[]): Partial<FormValues> {
  const fields: Partial<FormValues> = {};

  for (const prize of prizes) {
    const position = prize.position;
    fields[`prize${position}TypeId` as keyof FormValues] = prize.prizeTypeId as never;
    fields[`prize${position}Value` as keyof FormValues] = (prize.prizeValue ?? undefined) as never;
    fields[`prize${position}ImageUrl` as keyof FormValues] = (prize.prizeImageUrl ?? undefined) as never;
  }

  return fields;
}

interface PrizePositionFieldsProps {
  position: PrizePosition;
  compact: boolean;
  loadingPrizeTypes: boolean;
  prizeTypeSelectOptions: { value: string; label: string }[];
  onImageUpload: (position: PrizePosition, event: ChangeEvent<HTMLInputElement>) => void;
  onRemove?: () => void;
  form: ReturnType<typeof useForm>[0];
}

function PrizePositionFields({
  position,
  compact,
  loadingPrizeTypes,
  prizeTypeSelectOptions,
  onImageUpload,
  onRemove,
  form,
}: PrizePositionFieldsProps) {
  const typeField = `prize${position}TypeId` as const;
  const valueField = `prize${position}Value` as const;
  const imageField = `prize${position}ImageUrl` as const;
  const imageUrl = AntForm.useWatch(imageField, form);
  const isRequired = position === 1;

  return (
    <div className="rounded-lg border border-amber-200/80 bg-white/70 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-amber-900">
          {PRIZE_POSITION_LABELS[position]}
          {!isRequired && (
            <span className="ml-2 text-xs font-normal text-amber-700">(opcional)</span>
          )}
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-950"
          >
            <FaTimes className="text-[10px]" />
            Quitar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormItem
          name={typeField}
          label={<span className="text-sm font-medium text-gray-700">Tipo de premiación</span>}
          style={{ marginBottom: 0 }}
          rules={
            isRequired ? [{ required: true, message: "Selecciona un tipo de premiación" }] : []
          }
        >
          <Select
            allowClear={!isRequired}
            placeholder={
              loadingPrizeTypes
                ? "Cargando tipos de premiación..."
                : "Selecciona el tipo de premio"
            }
            size={compact ? "middle" : "large"}
            loading={loadingPrizeTypes}
            disabled={loadingPrizeTypes || prizeTypeSelectOptions.length === 0}
            options={prizeTypeSelectOptions}
            showSearch
            optionFilterProp="label"
            onChange={(value) => {
              if (!value) {
                form.setFieldsValue({
                  [valueField]: undefined,
                  [imageField]: undefined,
                });
              }
            }}
          />
        </FormItem>

        <FormItem
          name={valueField}
          label={<span className="text-sm font-medium text-gray-700">Valor del premio</span>}
          style={{ marginBottom: 0 }}
          rules={[
            ...(isRequired ? [{ required: true, message: "Indica el valor del premio" }] : []),
            { max: 255, message: "Máximo 255 caracteres" },
          ]}
        >
          <Input
            placeholder="Ej: 500.000 COP, Cena para 4 personas"
            size={compact ? "middle" : "large"}
          />
        </FormItem>

        <FormItem
          name={imageField}
          label={<span className="text-sm font-medium text-gray-700">Imagen del premio</span>}
          className="md:col-span-2"
          style={{ marginBottom: 0 }}
        >
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100">
                Subir imagen
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => onImageUpload(position, event)}
                />
              </label>
              {imageUrl && (
                <Button size="small" onClick={() => form.setFieldValue(imageField, undefined)}>
                  Quitar
                </Button>
              )}
            </div>
            {imageUrl && (
              <img
                src={imageUrl}
                alt={`Vista previa premio ${position}`}
                className="h-20 w-20 rounded-md border border-amber-200 object-cover"
              />
            )}
          </div>
        </FormItem>
      </div>
    </div>
  );
}

interface BettingPoolFormProps {
  mode: "create" | "edit";
  existingPool?: BettingPoolResponseDTO | BettingPoolWithTournamentResponseDTO;
  existingTournament?:
    | TournamentResponseDTO
    | { id: string; name: string; logo: string | null }
    | null;
  compact?: boolean;
}

export function BettingPoolForm({
  mode,
  existingPool,
  existingTournament,
  compact = false,
}: BettingPoolFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<
    | TournamentResponseDTO
    | { id: string; name: string; logo: string | null }
    | null
  >(existingTournament || null);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(
    existingPool?.seasonId || null,
  );
  const [resultRules, setResultRules] =
    useState<Record<ResultRuleType, RuleState>>(initialResultRules);
  const [bonusRules, setBonusRules] =
    useState<Record<BonusRuleType, RuleState>>(initialBonusRules);

  const RULE_PRIORITY: Record<ResultRuleType, number> = {
    exact_score: 1,
    winner: 2,
  };

  const [hasPredictions, setHasPredictions] = useState(false);

  const [predictionDeadline, setPredictionDeadline] = useState({
    minutes: DEFAULT_POOL_CONFIG.scoring.predictionDeadline?.minutes ?? 0,
  });
  const [tournamentWinnerConfig, setTournamentWinnerConfig] = useState<{
    enabled: boolean;
    points: number;
    deadlineAt: Dayjs | null;
  }>({
    enabled: DEFAULT_POOL_CONFIG.tournamentWinner?.enabled ?? false,
    points: DEFAULT_POOL_CONFIG.tournamentWinner?.points ?? 10,
    deadlineAt: null,
  });
  const [activePrizeTypes, setActivePrizeTypes] = useState<PrizeTypeResponseDTO[]>([]);
  const [loadingPrizeTypes, setLoadingPrizeTypes] = useState(true);
  const [visiblePrizePositions, setVisiblePrizePositions] = useState<PrizePosition[]>([1]);

  const prizeTypeSelectOptions = useMemo(() => {
    const options = activePrizeTypes.map((pt) => ({
      value: pt.id,
      label: pt.name,
    }));

    if (mode === "edit" && existingPool?.prizes?.length) {
      for (const prize of existingPool.prizes) {
        if (
          prize.prizeType &&
          !options.some((option) => option.value === prize.prizeType!.id)
        ) {
          options.unshift({
            value: prize.prizeType.id,
            label: `${prize.prizeType.name} (inactivo)`,
          });
        }
      }
    }

    return options;
  }, [activePrizeTypes, mode, existingPool]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingPrizeTypes(true);
        const response = await prizeTypeService.getPaginate({
          page: 1,
          pageSize: 100,
          isActive: true,
        });
        if (mounted && response.success && response.data) {
          setActivePrizeTypes(response.data.data);
        }
      } catch {
        if (mounted) {
          setActivePrizeTypes([]);
        }
      } finally {
        if (mounted) {
          setLoadingPrizeTypes(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (mode === "edit" && existingPool) {
      form.setFieldsValue({
        name: existingPool.name,
        description: existingPool.description || "",
        isPrivate: existingPool.isPrivate,
        ...mapPrizesToFormFields(existingPool.prizes ?? []),
      });

      if (existingPool.poolConfig?.scoring.resultRules) {
        const newRules = { ...disabledResultRules };
        existingPool.poolConfig.scoring.resultRules.forEach((rule) => {
          if (rule.type in newRules) {
            newRules[rule.type as ResultRuleType] = {
              enabled: true,
              points: rule.points,
              priority:
                rule.priority ?? RULE_PRIORITY[rule.type as ResultRuleType],
            };
          }
        });
        setResultRules(newRules);
      }

      if (existingPool.poolConfig?.scoring.bonusRules) {
        const newBonus = { ...disabledBonusRules };
        existingPool.poolConfig.scoring.bonusRules.forEach((rule) => {
          if (rule.type in newBonus) {
            newBonus[rule.type as BonusRuleType] = {
              enabled: true,
              points: rule.points,
              priority: newBonus[rule.type as BonusRuleType]?.priority ?? 0,
            };
          }
        });
        setBonusRules(newBonus);
      }

      if (existingPool.poolConfig?.scoring.predictionDeadline) {
        setPredictionDeadline({
          minutes: existingPool.poolConfig.scoring.predictionDeadline.minutes ?? 0,
        });
      }

      if (existingPool.poolConfig?.tournamentWinner) {
        const winnerConfig = existingPool.poolConfig.tournamentWinner;
        setTournamentWinnerConfig({
          enabled: winnerConfig.enabled,
          points: winnerConfig.points,
          deadlineAt: winnerConfig.deadlineAt
            ? dayjs(winnerConfig.deadlineAt)
            : null,
        });
      }

      setVisiblePrizePositions(deriveVisiblePrizePositions(existingPool.prizes));
    }
  }, [mode, existingPool, existingTournament, form]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mode === "edit" && existingPool) {
        try {
          const preds = await (
            await import("@/frontend/services/poolPredictionService")
          ).poolPredictionService.getByPool(existingPool.id);
          if (mounted) {
            setHasPredictions(!!(preds && preds.data && preds.data.length > 0));
          }
        } catch (err) {
          // ignore
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [mode, existingPool]);

  const handlePrizeImageFile = (
    position: PrizePosition,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      Message.error({ content: "Selecciona un archivo de imagen válido" });
      event.target.value = "";
      return;
    }

    if (file.size > MAX_PRIZE_IMAGE_BYTES) {
      Message.error({ content: "La imagen no debe superar 2 MB" });
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      form.setFieldValue(`prize${position}ImageUrl`, String(reader.result ?? ""));
    };
    reader.onerror = () => {
      Message.error({ content: "No se pudo leer la imagen seleccionada" });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleAddPrizePosition = () => {
    const next = nextAddablePrizePosition(visiblePrizePositions);
    if (!next) return;
    setVisiblePrizePositions((prev) => [...prev, next]);
  };

  const handleRemovePrizePosition = (position: PrizePosition) => {
    if (position === 1) return;

    const positionsToRemove =
      position === 2
        ? visiblePrizePositions.filter((item) => item >= 2)
        : [position];

    setVisiblePrizePositions((prev) =>
      prev.filter((item) => !positionsToRemove.includes(item)),
    );

    const clearedFields: Partial<FormValues> = {};
    for (const item of positionsToRemove) {
      clearedFields[`prize${item}TypeId` as keyof FormValues] = undefined as never;
      clearedFields[`prize${item}Value` as keyof FormValues] = undefined as never;
      clearedFields[`prize${item}ImageUrl` as keyof FormValues] = undefined as never;
    }
    form.setFieldsValue(clearedFields);
  };

  const handleTournamentSelect = (tournament: TournamentResponseDTO) => {
    setSelectedTournament(tournament);
    form.setFieldValue("tournamentId", tournament.id);

    const currentSeason = tournament.seasons?.find((s) => s.isCurrent);
    if (currentSeason) {
      setSelectedSeasonId(currentSeason.id);
      form.setFieldValue("seasonId", currentSeason.id);
    } else if (tournament.seasons && tournament.seasons.length === 1) {
      const onlySeason = tournament.seasons[0];
      if (onlySeason) {
        setSelectedSeasonId(onlySeason.id);
        form.setFieldValue("seasonId", onlySeason.id);
      }
    } else {
      setSelectedSeasonId(null);
      form.setFieldValue("seasonId", undefined);
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!user?.id) {
      Message.error({ content: "Usuario no autenticado" });
      return;
    }

    if (!selectedTournament && mode === "create") {
      Message.warning({ content: "Debes seleccionar un torneo" });
      return;
    }

    if (!selectedSeasonId && mode === "create") {
      Message.warning({ content: "Debes seleccionar una temporada" });
      return;
    }

    const configuredResultRules = (Object.keys(resultRules) as ResultRuleType[])
      .filter((rule) => resultRules[rule].enabled)
      .map((rule) => ({
        type: rule,
        points: Number(resultRules[rule].points),
        priority: Number(resultRules[rule].priority),
      }));

    const configuredBonusRules = (Object.keys(bonusRules) as BonusRuleType[])
      .filter((rule) => rule !== "bracket_winner" && bonusRules[rule].enabled)
      .map((rule) => ({
        type: rule,
        points: Number(bonusRules[rule].points),
        ...(rule === "unique_bonus"
          ? { condition: { type: "exact_score" as const, isUniqueHit: true } }
          : {}),
      }));

    if (configuredResultRules.length === 0) {
      Message.warning({
        content: "Debes seleccionar una regla principal",
      });
      return;
    }

    if (tournamentWinnerConfig.enabled && !tournamentWinnerConfig.deadlineAt) {
      Message.warning({
        content: "Selecciona la fecha límite para predecir el campeón del torneo",
      });
      return;
    }

    const tournamentWinnerPayload = {
      enabled: tournamentWinnerConfig.enabled,
      points: Number(tournamentWinnerConfig.points),
      deadlineAt:
        tournamentWinnerConfig.enabled && tournamentWinnerConfig.deadlineAt
          ? tournamentWinnerConfig.deadlineAt.endOf("day").toISOString()
          : null,
    };

    try {
      setLoading(true);

      if (mode === "create") {
        const payload: CreateBettingPoolRequestDTO = {
          name: values.name,
          description: values.description ?? null,
          seasonId: selectedSeasonId!,
          prizes: buildPrizesFromForm(values),
          isPrivate: values.isPrivate,
          poolConfig: {
            scoring: {
              resultRules: configuredResultRules,
              bonusRules: configuredBonusRules,
              defaultPoints: 0,
              predictionDeadline: {
                hours: 0,
                minutes: predictionDeadline.minutes,
                days: 0,
              },
            },
            privacity: {
              showOthersPredictions: "after_lock",
            },
            tournamentWinner: tournamentWinnerPayload,
          },
        };

        const response = await bettingPoolService.create(payload);
        if (response.success) {
          Message.success({ content: "Polla creada correctamente" });
          navigate("/sports-predictions/pools/my-pools");
        } else {
          Message.error({
            content: response.error || "No se pudo crear la polla",
          });
        }
      } else {
        const payload: UpdateBettingPoolRequestDTO = {
          name: values.name,
          description: values.description ?? null,
          prizes: buildPrizesFromForm(values),
          isPrivate: values.isPrivate,
          poolConfig: {
            scoring: {
              resultRules: configuredResultRules,
              bonusRules: configuredBonusRules,
              defaultPoints: 0,
              predictionDeadline: {
                hours: 0,
                minutes: predictionDeadline.minutes,
                days: 0,
              },
            },
            privacity: {
              showOthersPredictions: "after_lock",
            },
            tournamentWinner: tournamentWinnerPayload,
          },
        };

        const response = await bettingPoolService.update(
          existingPool!.id,
          payload,
        );
        if (response.success) {
          Message.success({ content: "Pool actualizado correctamente" });
          navigate(`/sports-predictions/pools/${existingPool!.id}`);
        } else {
          Message.error({
            content: response.error || "No se pudo actualizar el pool",
          });
        }
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error
            ? error.message
            : mode === "create"
              ? "No se pudo crear la polla"
              : "No se pudo actualizar el pool",
      });
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = mode === "edit";

  const poolSeason =
    existingPool && "season" in existingPool ? existingPool.season : null;

  const nextPrizePosition = nextAddablePrizePosition(visiblePrizePositions);

  return (
    <div className={compact ? "" : "space-y-6"}>
      {!compact && (
        <div className="bg-gradient-to-r from-emerald-600 to-teal-500 rounded-xl p-6 text-white shadow">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FaTrophy className="text-yellow-300" />
            {isEditMode ? "Editar grupo de apuesta" : "Crear grupo de apuesta"}
          </h2>
          <p className="text-emerald-100 mt-1 text-sm">
            {isEditMode
              ? "Modifica la configuración del pool. El torneo no puede ser cambiado."
              : "Define nombre, torneo y reglas de puntuación para la polla."}
          </p>
        </div>
      )}

      {isEditMode && !compact && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="mt-0.5 text-blue-500 text-lg">
            <FaLock />
          </div>
          <p className="text-sm text-blue-800">
            <strong>Torneo bloqueado:</strong> El torneo y la temporada no
            pueden ser modificados una vez creado el grupo de predicciones.
          </p>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{ isPrivate: false }}
      >
        {/* ── Información básica ── */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
            <FaFutbol className="text-emerald-500 text-sm" />
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Información básica
            </span>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormItem
                name="name"
                label={<span className="text-sm font-medium text-gray-700">Nombre del grupo</span>}
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: "El nombre es requerido" }]}
              >
                <Input
                  placeholder="Ej: Polla Mundial Oficina"
                  size={compact ? "middle" : "large"}
                />
              </FormItem>

              {isEditMode && !compact ? (
                <FormItem label={<span className="text-sm font-medium text-gray-700">Torneo</span>} style={{ marginBottom: 0 }}>
                  <Input
                    value={`${existingTournament?.name} - ${poolSeason?.season || "Sin temporada"}`}
                    disabled
                    size="large"
                    className="bg-gray-50"
                  />
                </FormItem>
              ) : (
                <FormItem
                  name="description"
                  label={<span className="text-sm font-medium text-gray-700">Descripción</span>}
                  style={{ marginBottom: 0 }}
                >
                  <TextArea rows={compact ? 2 : 3} placeholder="Descripción opcional del grupo" />
                </FormItem>
              )}

              {!isEditMode && (
                <FormItem
                  name="tournamentId"
                  label={<span className="text-sm font-medium text-gray-700">Torneo</span>}
                  style={{ marginBottom: 0 }}
                  className="md:col-span-2"
                  rules={[{ required: true, message: "Selecciona un torneo" }]}
                >
                  <SearchPaginatedSelect<TournamentResponseDTO>
                    placeholder="Buscar torneo..."
                    fetchFn={tournamentService.getPaginate}
                    filterField="name"
                    onSelect={handleTournamentSelect}
                    renderItem={(t) => (
                      <div className="flex items-center gap-2">
                        {t.logo && (
                          <img src={t.logo} alt={t.name} className="w-5 h-5 object-contain" />
                        )}
                        <span>
                          {t.name} (
                          {t.seasons && t.seasons.length > 0
                            ? t.seasons![0]!.season
                            : "Sin temporada"}
                          )
                        </span>
                      </div>
                    )}
                    getItemKey={(t) => t.id}
                    minLength={2}
                    debounceMs={250}
                    allowButtonEmptyQuery={true}
                    errorMessage="Error al buscar torneos"
                  />
                </FormItem>
              )}

              {selectedTournament && !isEditMode && (
                <div className="md:col-span-2 text-sm text-gray-600">
                  Torneo seleccionado: <strong>{selectedTournament.name}</strong>
                </div>
              )}

              {isEditMode && !compact && (
                <FormItem
                  name="description"
                  label={<span className="text-sm font-medium text-gray-700">Descripción</span>}
                  style={{ marginBottom: 0 }}
                  className="md:col-span-2"
                >
                  <TextArea rows={3} placeholder="Descripción opcional del grupo" />
                </FormItem>
              )}
            </div>

            <div className="rounded-xl border border-amber-100 bg-gradient-to-br from-amber-50/80 to-orange-50/40 p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-amber-900">
                  <FaGift className="text-amber-500" />
                  <span className="text-sm font-semibold uppercase tracking-wide">
                    Premiación
                  </span>
                </div>
                {nextPrizePosition && (
                  <Button
                    type="default"
                    size="small"
                    icon={<FaPlus className="text-xs" />}
                    onClick={handleAddPrizePosition}
                  >
                    Agregar premio {PRIZE_POSITION_LABELS[nextPrizePosition]}
                  </Button>
                )}
              </div>

              <p className="text-xs text-amber-800/80">
                El premio de 1er puesto es obligatorio. Puedes añadir hasta 2 premios
                adicionales para 2do y 3er lugar.
              </p>

              <div className="space-y-4 pt-1 border-t border-amber-200/80">
                {visiblePrizePositions.map((position) => (
                  <PrizePositionFields
                    key={position}
                    position={position}
                    compact={compact}
                    loadingPrizeTypes={loadingPrizeTypes}
                    prizeTypeSelectOptions={prizeTypeSelectOptions}
                    onImageUpload={handlePrizeImageFile}
                    onRemove={
                      position === 1 ? undefined : () => handleRemovePrizePosition(position)
                    }
                    form={form}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-6 pt-1 border-t border-gray-100">
              <FormItem name="isPrivate" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Checkbox>
                  <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-1.5">
                    <FaLock className="text-gray-400 text-xs" />
                    Grupo privado
                  </span>
                </Checkbox>
              </FormItem>

              <div className="flex items-center gap-2">
                <FaClock className="text-gray-400 text-sm" />
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Cierre de predicciones
                </span>
                <InputNumber
                  min={0}
                  className="w-16"
                  value={predictionDeadline.minutes}
                  onChange={(value) =>
                    setPredictionDeadline((prev) => ({
                      ...prev,
                      minutes: Number(value ?? 0),
                    }))
                  }
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  min antes del partido
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Predicción de campeón ── */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
            <FaTrophy className="text-amber-500 text-sm" />
            <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Predicción de campeón del torneo
            </span>
          </div>

          <div className="p-4 space-y-4">
            <Checkbox
              checked={tournamentWinnerConfig.enabled}
              onChange={(e) =>
                setTournamentWinnerConfig((prev) => ({
                  ...prev,
                  enabled: e.target.checked,
                }))
              }
            >
              <span className="text-sm font-medium text-gray-700">
                Permitir que los participantes elijan el ganador del torneo
              </span>
            </Checkbox>

            {tournamentWinnerConfig.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">
                    Puntos por acertar el campeón
                  </label>
                  <InputNumber
                    min={0}
                    className="w-full"
                    value={tournamentWinnerConfig.points}
                    onChange={(value) =>
                      setTournamentWinnerConfig((prev) => ({
                        ...prev,
                        points: Number(value ?? 0),
                      }))
                    }
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">
                    Fecha límite para elegir campeón
                  </label>
                  <DatePicker
                    className="w-full"
                    format="DD/MM/YYYY"
                    placeholder="Ej: 15/06/2026"
                    value={tournamentWinnerConfig.deadlineAt}
                    disabledDate={(current) =>
                      current ? current < dayjs().startOf("day") : false
                    }
                    onChange={(value) =>
                      setTournamentWinnerConfig((prev) => ({
                        ...prev,
                        deadlineAt: value,
                      }))
                    }
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Los participantes podrán elegir hasta el final de ese día.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Reglas de puntuación ── */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FaStar className="text-yellow-400 text-sm" />
              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Reglas de puntuación
              </span>
            </div>
            {hasPredictions && (
              <div className="flex items-center gap-1.5 text-amber-600 text-xs font-medium bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                <FaExclamationTriangle className="text-xs" />
                Reglas principales bloqueadas
              </div>
            )}
          </div>

          <div className="p-4 space-y-4">
            {/* Reglas principales */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Reglas principales — elige una o ambas
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(Object.keys(resultRules) as ResultRuleType[]).map((rule) => {
                  const disabledDuePredictions =
                    hasPredictions &&
                    (rule === "exact_score" || rule === "winner");
                  const isEnabled = resultRules[rule].enabled;
                  return (
                    <div
                      key={rule}
                      className={[
                        "relative rounded-xl border-2 transition-all duration-150",
                        isEnabled
                          ? "border-emerald-400 bg-emerald-50"
                          : "border-gray-200 bg-gray-50 opacity-70",
                        disabledDuePredictions ? "cursor-not-allowed" : "cursor-pointer",
                      ].join(" ")}
                    >
                      <div className="p-3">
                        <div className="flex items-start justify-between mb-1">
                          <Checkbox
                            checked={isEnabled}
                            disabled={disabledDuePredictions}
                            onChange={(e) =>
                              setResultRules((prev) => ({
                                ...prev,
                                [rule]: {
                                  ...prev[rule],
                                  enabled: e.target.checked,
                                },
                              }))
                            }
                          >
                            <span className={`text-sm font-semibold ${isEnabled ? "text-emerald-800" : "text-gray-500"}`}>
                              {RESULT_RULE_LABELS[rule]}
                            </span>
                          </Checkbox>
                          {isEnabled && (
                            <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full font-medium">
                              Activa
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 ml-6 mb-3">
                          {RESULT_RULE_DESCRIPTIONS[rule]}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-medium text-gray-500 mb-1 block">
                              Puntos
                            </label>
                            <InputNumber
                              min={0}
                              disabled={!isEnabled || disabledDuePredictions}
                              value={resultRules[rule].points}
                              onChange={(value) =>
                                setResultRules((prev) => ({
                                  ...prev,
                                  [rule]: {
                                    ...prev[rule],
                                    points: Number(value ?? 0),
                                  },
                                }))
                              }
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 mb-1 block">
                              Prioridad
                            </label>
                            <InputNumber
                              min={1}
                              disabled={!isEnabled || disabledDuePredictions}
                              value={resultRules[rule].priority}
                              onChange={(value) =>
                                setResultRules((prev) => ({
                                  ...prev,
                                  [rule]: {
                                    ...prev[rule],
                                    priority: Number(value ?? 0),
                                  },
                                }))
                              }
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bonos */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Bonos
                </p>
                <FaGift className="text-purple-400 text-xs" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(Object.keys(bonusRules) as BonusRuleType[])
                  .filter((rule) => rule !== "bracket_winner")
                  .map((rule) => {
                    const isEnabled = bonusRules[rule].enabled;
                    return (
                      <div
                        key={rule}
                        className={[
                          "rounded-xl border-2 transition-all duration-150",
                          isEnabled
                            ? "border-purple-300 bg-purple-50"
                            : "border-gray-200 bg-gray-50 opacity-70",
                        ].join(" ")}
                      >
                        <div className="p-3">
                          <div className="flex items-start justify-between mb-1">
                            <Checkbox
                              checked={isEnabled}
                              onChange={(e) =>
                                setBonusRules((prev) => ({
                                  ...prev,
                                  [rule]: {
                                    ...prev[rule],
                                    enabled: e.target.checked,
                                  },
                                }))
                              }
                            >
                              <span className={`text-sm font-semibold ${isEnabled ? "text-purple-800" : "text-gray-500"}`}>
                                {BONUS_RULE_LABELS[rule]}
                              </span>
                            </Checkbox>
                          </div>
                          <p className="text-xs text-gray-500 ml-6 mb-3">
                            {BONUS_RULE_DESCRIPTIONS[rule]}
                          </p>
                          <div>
                            <label className="text-xs font-medium text-gray-500 mb-1 block">
                              Puntos extra
                            </label>
                            <InputNumber
                              min={0}
                              disabled={!isEnabled}
                              value={bonusRules[rule].points}
                              onChange={(value) =>
                                setBonusRules((prev) => ({
                                  ...prev,
                                  [rule]: {
                                    ...prev[rule],
                                    points: Number(value ?? 0),
                                  },
                                }))
                              }
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Acciones ── */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={() =>
              isEditMode
                ? navigate(`/sports-predictions/pools/${existingPool!.id}`)
                : navigate("/sports-predictions/pools/my-pools")
            }
          >
            Cancelar
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEditMode ? "Guardar cambios" : "Crear grupo"}
          </Button>
        </div>
      </Form>
    </div>
  );
}
